import * as React from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Popper } from "@mui/base/Popper";
import Autocomplete from "@mui/joy/Autocomplete";
import AutocompleteListbox from "@mui/joy/AutocompleteListbox";
import AutocompleteOption from "@mui/joy/AutocompleteOption";
import ListSubheader from "@mui/joy/ListSubheader";
import {
  CircularProgress,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from "@mui/joy";
import {
  AgencySearchFragment,
  useAgencyListQuery,
} from "../__generated__/types-and-hooks";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "../i18n";

const LISTBOX_PADDING = 6; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="li" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <AutocompleteOption {...dataSet[0]} style={inlineStyle}>
      <ListItemDecorator>
        <Typography level="body-xs">#{dataSet[1].id}</Typography>
      </ListItemDecorator>
      <ListItemContent sx={{ fontSize: "sm" }}>
        {dataSet[1]?.name}
        <Typography level="body-xs">{dataSet[1].name}</Typography>
      </ListItemContent>
    </AutocompleteOption>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return (
    <AutocompleteListbox
      {...props}
      {...outerProps}
      component="div"
      ref={ref}
      sx={{
        "& ul": {
          padding: 0,
          margin: 0,
          flexShrink: 0,
        },
      }}
    />
  );
});

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  {
    anchorEl: any;
    open: boolean;
    modifiers: any[];
  } & React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, anchorEl, open, modifiers, ...other } = props;
  const itemData: Array<any> = [];
  (
    children as [
      Array<{ children: Array<React.ReactElement<any>> | undefined }>
    ]
  )[0].forEach((item) => {
    if (item) {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  });

  const itemCount = itemData.length;
  const itemSize = 40;

  return (
    <Popper
      style={{ zIndex: 1000 }}
      ref={ref}
      anchorEl={anchorEl}
      open={open}
      modifiers={modifiers}
    >
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={itemSize * 8}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </Popper>
  );
});

export interface QAgencyProps {
  hasFocus?: boolean;
  agency: { id: number; name: string } | null;
  update: (
    agency: AgencySearchFragment | null
  ) => Promise<string | null | undefined>;
}

export function QAgency({ hasFocus = false, update, agency }: QAgencyProps) {
  const t = useTranslation();
  const [intVal, setIntVal] = React.useState(agency);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setIntVal(agency);
  }, [agency]);

  React.useEffect(() => {
    if (hasFocus) {
      inputRef?.current?.focus();
    }
  }, [hasFocus]);

  const handleChanges = React.useCallback(
    async (e: any, value: AgencySearchFragment | null) => {
      inputRef.current?.blur();
      setLoading(true);
      setIntVal(value);
      await update(value);
      // setIntErr(err);
      setLoading(false);
    },
    [update]
  );

  const agencyListQuery = useAgencyListQuery({});
  const isLoading = agencyListQuery.loading;
  const agencies = agencyListQuery.data?.team_list?.teams;

  return (
    <Autocomplete
      size="sm"
      sx={{ minWidth: "250px", width: "100%", fontSize: "14px" }}
      value={agencies?.find((s) => s.id === intVal?.id) ?? intVal}
      loading={isLoading || loading}
      disableListWrap
      startDecorator={<SearchIcon />}
      placeholder={t("Agencies...")}
      onChange={handleChanges as any}
      slotProps={{
        input: {
          ref: inputRef,
        },
      }}
      endDecorator={loading && <CircularProgress size="sm" value={25} />}
      slots={{
        listbox: ListboxComponent,
      }}
      options={agencies ?? []}
      groupBy={(option) => option.name.split(/[^0-9a-zA-Z]/)[0].toUpperCase()}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => [props, option] as React.ReactNode}
      // TODO: Post React 18 update - validate this conversion, look like a hidden bug
      renderGroup={(params) => params as unknown as React.ReactNode}
      // filterOptions={filterOptions}
    />
  );
}
